// @ts-nocheck
import { EventScheduleTabs } from '@/components/public/EventScheduleTabs';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, DollarSign, MapPin, Clock, Users, LayoutList, Share2, ClipboardCheck, Printer, Trophy, ShoppingBag } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatCalendarDate } from "@/lib/dates";
interface Venue {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

interface Event {
  id: string;
  slug?: string;
  name: string;
  description?: string;
  start_date: string;
  end_date?: string;
  registration_close_date?: string;
  status: string;
  thumbnail_url?: string;
  gender?: string;
  sport_id?: string;
  venue_name?: string;
  venue_city?: string;
  venue_state?: string;
  venue_address?: string;
  venue_zip?: string;
  main_venue_id?: string;
  additional_venue_ids?: string[];
  check_in_instructions_html?: string;
  venue?: {
    name: string;
    city: string;
    state: string;
  };
  main_venue?: Venue;
  additional_venues?: Venue[];
  event_category_mappings: Array<{
    event_categories: {
      id: string;
      name: string;
    };
  }>;
  age_groups?: Array<{
    id: string;
    name: string;
    registration_fee: string;
  }>;
  pricing_tiers?: Array<{
    id: string;
    tier_name: string;
    price: string;
  }>;
  // Add new fields from Event Details tab
  age_groups_description?: string;
  pricing_notes?: string;
  event_format?: string;
  accept_online_registrations?: boolean;
  external_registration_url?: string;
  pricing_display_text?: string;
  external_schedule_url?: string;
}

interface EventSidebarProps {
  event: Event;
}

const EventSidebar: React.FC<EventSidebarProps> = ({ event }) => {
  const [hasSchedule, setHasSchedule] = useState(false);
  const [tournamentId, setTournamentId] = useState<string | null>(null);
  const [bracketPublished, setBracketPublished] = useState(false);
  const [hasExtras, setHasExtras] = useState(false);

  // Helper to get proper image URL for featured/thumbnail images
  const getImageUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Use event-featured bucket (where FeaturedImageUpload stores images)
    const { data } = supabase.storage.from('event-featured').getPublicUrl(url);
    return data.publicUrl;
  };

  useEffect(() => {
    const checkForSchedule = async () => {
      if (!event?.id) return;
      
      // Get tournaments for this event that have scheduled matches
      const { data: tournaments } = await supabase
        .from('tournaments')
        .select('id, bracket_published')
        .eq('event_id', event.id)
        .in('status', ['active', 'setup', 'completed']);
      
      if (tournaments && tournaments.length > 0) {
        // Check each tournament to find one with scheduled matches
        for (const tournament of tournaments) {
          const { count } = await supabase
            .from('tournament_matches')
            .select('id', { count: 'exact', head: true })
            .eq('tournament_id', tournament.id)
            .not('scheduled_time', 'is', null);
          
          if (count && count > 0) {
            setHasSchedule(true);
            setTournamentId(tournament.id);
            setBracketPublished(!!(tournament as any).bracket_published);
            return; // Found one, no need to check more
          }
        }
      }
    };
    
    checkForSchedule();
  }, [event?.id]);

  useEffect(() => {
    const checkExtras = async () => {
      if (!event?.id) return;
      const { count } = await supabase
        .from('event_extras')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', event.id)
        .eq('is_active', true);
      setHasExtras((count || 0) > 0);
    };
    checkExtras();
  }, [event?.id]);

  const formatDate = (dateStr: string) => {
    // Parse the date string and treat it as local date to avoid timezone issues
    return formatCalendarDate(dateStr, 'EEEE, MMMM d, yyyy');
  };

  const getLowestPrice = () => {
    const prices: number[] = [];
    
    // Get prices from age groups
    if (event.age_groups && event.age_groups.length > 0) {
      event.age_groups.forEach(group => {
        const price = parseFloat(group.registration_fee);
        if (!isNaN(price) && price > 0) {
          prices.push(price);
        }
      });
    }
    
    // Get prices from pricing tiers
    if (event.pricing_tiers && event.pricing_tiers.length > 0) {
      event.pricing_tiers.forEach(tier => {
        const price = parseFloat(tier.price);
        if (!isNaN(price) && price > 0) {
          prices.push(price);
        }
      });
    }
    
    if (prices.length === 0) {
      return null;
    }
    
    return Math.min(...prices);
  };

  const handleShare = (platform: string) => {
    const currentUrl = window.location.href;
    const eventTitle = encodeURIComponent(event.name);
    const eventDescription = encodeURIComponent(event.description || `Join us for ${event.name}`);
    
    switch (platform) {
      case 'Facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`, '_blank');
        break;
      case 'Twitter':
        window.open(`https://twitter.com/intent/tweet?url=${currentUrl}&text=${eventTitle}`, '_blank');
        break;
      case 'LinkedIn':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${currentUrl}`, '_blank');
        break;
      case 'Email':
        window.location.href = `mailto:?subject=${eventTitle}&body=Check out this event: ${currentUrl}`;
        break;
    }
  };

  const shareButtons = [
    { name: 'Facebook', color: 'bg-blue-600' },
    { name: 'Twitter', color: 'bg-blue-400' },
    { name: 'LinkedIn', color: 'bg-blue-800' },
    { name: 'Email', color: 'bg-gray-600' }
  ];

  const formatVenueAddress = (venue: Venue) => {
    const parts = [];
    if (venue.address) parts.push(venue.address);
    if (venue.city) parts.push(venue.city);
    if (venue.state) parts.push(venue.state);
    if (venue.zip_code) parts.push(venue.zip_code);
    return parts.join(', ');
  };

  const formatMainVenueAddress = () => {
    const parts = [];
    if (event.venue_address) parts.push(event.venue_address);
    if (event.venue_city) parts.push(event.venue_city);
    if (event.venue_state) parts.push(event.venue_state);
    if (event.venue_zip) parts.push(event.venue_zip);
    return parts.join(', ');
  };

  const hasVenueInfo = () => {
    // Check if we have main venue info (either from direct fields or main_venue object)
    const hasMainVenue = event.venue_name || event.main_venue?.name;
    // Check if we have additional venues
    const hasAdditionalVenues = event.additional_venues && event.additional_venues.length > 0;
    // Check fallback venue info for old events
    const hasFallbackVenue = event.venue?.name;
    
    return hasMainVenue || hasAdditionalVenues || hasFallbackVenue;
  };

  const hasMainVenue = () => {
    return event.main_venue?.name || event.venue_name || event.venue?.name;
  };

  const hasAdditionalVenues = () => {
    return event.additional_venues && event.additional_venues.length > 0;
  };

  return (
    <div className="space-y-6">
      {/* Featured Image */}
      {event.thumbnail_url && (
        <div className="rounded-xl overflow-hidden shadow">
          <img
            src={getImageUrl(event.thumbnail_url)}
            alt={event.name}
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {/* Event Date */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Event Date
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm space-y-3">
            <div>
              <div className="font-medium">Start Date</div>
              <div className="text-gray-600">{formatDate(event.start_date)}</div>
            </div>
            
            {event.end_date && (
              <div>
                <div className="font-medium">End Date</div>
                <div className="text-gray-600">{formatDate(event.end_date)}</div>
              </div>
            )}
            
            {event.registration_close_date && (
              <div>
                <div className="font-medium">Registration Closes</div>
                <div className="text-gray-600">{formatDate(event.registration_close_date)}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pricing Summary */}
      {(getLowestPrice() !== null || event.pricing_display_text) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Cost Starting From
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getLowestPrice() !== null ? (
              <div className="text-2xl font-bold text-primary">${getLowestPrice()}</div>
            ) : event.pricing_display_text ? (
              <div className="text-2xl font-bold text-primary">{event.pricing_display_text}</div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Register Button */}
      <Button
        className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3"
        onClick={() => {
          if (!event.accept_online_registrations && event.external_registration_url) {
            window.open(event.external_registration_url, '_blank');
          } else {
            const slug = event.slug || event.id;
            window.location.href = `/events/${slug}/register`;
          }
        }}
      >
        Register Now
      </Button>

      {/* View Schedule & Scores Button - external URL or internal bracket */}
      {event.external_schedule_url ? (
        <Button
          variant="outline"
          className="w-full border-green-600 text-green-600 hover:bg-green-50 font-semibold py-3"
          onClick={() => window.open(event.external_schedule_url, '_blank')}
        >
          <Trophy className="w-4 h-4 mr-2" />
          View Schedule & Scores
        </Button>
      ) : hasSchedule && tournamentId && bracketPublished ? (
        <Button
          variant="outline"
          className="w-full border-green-600 text-green-600 hover:bg-green-50 font-semibold py-3"
          onClick={() => window.open(`/tournaments/${tournamentId}/bracket`, '_blank')}
        >
          <Trophy className="w-4 h-4 mr-2" />
          View Schedule & Scores
        </Button>
      ) : null}

      {/* Age Groups */}
      {event.age_groups_description && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Age Groups
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="text-sm text-gray-600 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: event.age_groups_description }}
            />
          </CardContent>
        </Card>
      )}

      {/* Pricing */}
      {event.pricing_notes && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Pricing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="text-sm text-gray-600 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: event.pricing_notes }}
            />
          </CardContent>
        </Card>
      )}

      <EventScheduleTabs eventId={event.id} />

      {/* Format */}
      {event.event_format && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <LayoutList className="w-5 h-5" />
              Format
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="text-sm text-gray-600 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_p]:mb-2 [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: event.event_format }}
            />
          </CardContent>
        </Card>
      )}

      {/* Check-In Instructions */}
      {event.check_in_instructions_html && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              Check-In Instructions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="text-sm text-gray-600 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_p]:mb-2 [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: event.check_in_instructions_html }}
            />
          </CardContent>
        </Card>
      )}

      {/* Unified Venues Block */}
      {hasVenueInfo() && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              📍 Venues
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Main Venue Section */}
            {hasMainVenue() && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Tournament Headquarters</h4>
                <div className="text-sm space-y-1 pl-4">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 text-gray-500 flex-shrink-0" />
                    <div>
                      <div className="font-medium">
                        {event.main_venue?.name || event.venue_name || event.venue?.name}
                      </div>
                      {(() => {
                        let address = '';
                        if (event.main_venue) {
                          address = formatVenueAddress(event.main_venue);
                        } else if (event.venue_name) {
                          address = formatMainVenueAddress();
                        } else if (event.venue) {
                          address = `${event.venue.city}, ${event.venue.state}`;
                        }
                        return address && (
                          <div className="text-gray-600">{address}</div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Venues Section */}
            {hasAdditionalVenues() && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Additional Venues</h4>
                <div className="space-y-3 pl-4">
                  {event.additional_venues!.map((venue) => (
                    <div key={venue.id} className="text-sm">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-0.5 text-gray-500 flex-shrink-0" />
                        <div>
                          <div className="font-medium">{venue.name}</div>
                          {formatVenueAddress(venue) && (
                            <div className="text-gray-600">{formatVenueAddress(venue)}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Event Extras */}
      {hasExtras && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              Event Extras
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Video packages, photo coverage, event insurance & more.
            </p>
            <Button
              className="w-full"
              onClick={() => {
                const slug = event.slug || event.id;
                window.location.href = `/event-extras/${slug}`;
              }}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Shop Extras
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Share Buttons */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share Event
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {shareButtons.map((button) => (
              <Button
                key={button.name}
                variant="outline"
                size="sm"
                className={`${button.color} text-white border-0 hover:opacity-90`}
                onClick={() => handleShare(button.name)}
              >
                {button.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default EventSidebar;
